import { Observable, of, UnaryFunction } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';

/**
 * When the source emits true (loading), it emits immediately.
 * When the source emits false (not loading), it delays emitting false by the specified time.
 *
 * This is useful for preventing flickering of "no data" messages when loading completes quickly.
 *
 * Usage:
 * ```ts
 * isLoading$ = this.loadingState$.pipe(
 *   rxDelayLoadingState(1000) // delay false by 1 second
 * );
 * ```
 */
export function rxDelayLoadingState(
  time: number = 1000,
): UnaryFunction<Observable<boolean>, Observable<boolean>> {
  return (source: Observable<boolean>) => {
    return source.pipe(
      switchMap(
        (isLoading) =>
          isLoading
            ? of(true) // emit true immediately
            : of(false).pipe(delay(time)), // delay the false emission
      ),
    );
  };
}
